'use client'

import type { DarkModeType } from '@cloud/shared/ui'
import { useSettings } from '@cloud/shared/ui'
import { useLocale, useLocaleDirection, useTranslations } from 'gt-react'
import { MoonStar, Sun, SunMoon } from 'lucide-react'
import { useCallback } from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const modeIcons = {
  light: Sun,
  dark: MoonStar,
  system: SunMoon,
}

export function ModeDropdown() {
  const t = useTranslations('navigation.mode')
  const { settings, updateSettings } = useSettings()
  const locale = useLocale()
  const direction = useLocaleDirection(locale)

  const darkMode = settings.darkMode
  const ModeIcon = modeIcons[darkMode] || modeIcons.system

  const setMode = useCallback(
    (modeName: DarkModeType) => {
      updateSettings({ ...settings, darkMode: modeName })
    },
    [settings, updateSettings],
  )

  return (
    <DropdownMenu dir={direction}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Mode">
          <ModeIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>{t('mode')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={darkMode}>
          <DropdownMenuRadioItem value="light" onClick={() => setMode('light')}>
            {t('light')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" onClick={() => setMode('dark')}>
            {t('dark')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" onClick={() => setMode('system')}>
            {t('system')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
